import { AiCompanionReviewModeEnum } from "@simbelapp/order-sdk";
import { defineStore } from "pinia";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useTrainingPartnersStore } from "~/store/training-partners/training-partners.store";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import { TrainingPrefillFieldEnum } from "~/utils/enums/training-prefill.enums";
import { SessionItemFormatEnum } from "~/utils/enums/session-items.enums";
import { TrainingNavEnum, TrainingTypeEnum } from "~/utils/enums/training.enums";
import type {
  ITrainingPrefill,
  ITrainingPrefillState,
  ITrainingPrefillValues,
} from "~/utils/interfaces/training-prefill-interfaces";

/** Form types the review can target — an LMS/builder training opens another modal entirely. */
const REVIEWABLE_TRAINING_TYPES = [TrainingTypeEnum.EXTERNAL, TrainingTypeEnum.INTERNAL];

/**
 * Mirrors the partner dropdown's setter: the contact email follows the partner, otherwise the save
 * would push the previous partner's email onto the new one (`updateContactEmail`). Read-only lookup —
 * an unknown name is created at save time, exactly as a typed-in partner is.
 */
async function applyPartner(name: string | null) {
  const { general, disabled } = useCreateTrainingStore().training;

  const wanted = name?.trim().toLowerCase();
  const { data } = wanted
    ? await useTrainingPartnersStore().searchTrainingPartners({ search: name, limit: 10 })
    : { data: [] };
  const partner = data.find((candidate) => candidate.name?.trim().toLowerCase() === wanted) ?? null;

  if (partner) {
    general.trainingPartner = {
      trainingPartnerId: partner.trainingPartnerId,
      name: partner.name,
      mainContactEmail: partner.mainContactEmail,
    };
    general.mainContactEmail = partner.mainContactEmail ?? null;
    disabled.mainContactEmail = !!partner.mainContactEmail;
  } else {
    general.trainingPartner = name ? { trainingPartnerId: null, name } : null;
    general.mainContactEmail = null;
    disabled.mainContactEmail = false;
  }
}

/** Writes only the fields present — an absent key leaves the form's (stored) value alone. */
async function applyValues(values: ITrainingPrefillValues) {
  const { general, content, sessionItems } = useCreateTrainingStore().training;

  if (values.providerName !== undefined) await applyPartner(values.providerName);
  if (values.title !== undefined) general.title = values.title;
  if (values.externalUrl !== undefined) general.externalUrl = values.externalUrl;
  if (values.durationHours !== undefined) general.durationHours = values.durationHours;
  if (values.durationDays !== undefined) general.durationDays = values.durationDays;
  if (values.targetAudience !== undefined) content.targetAudience = values.targetAudience;
  if (values.goals !== undefined) content.goals = [...(values.goals ?? [])];
  if (values.program !== undefined) content.program = values.program;
  if (values.format !== undefined) general.format = values.format as SessionItemFormatEnum | null;

  if (values.priceInterExclTax !== undefined) {
    sessionItems.individual.priceExclTax = values.priceInterExclTax;
    sessionItems.individual.isChecked = true;
  }
  if (values.priceIntraExclTax !== undefined) {
    sessionItems.group.priceExclTax = values.priceIntraExclTax;
    sessionItems.group.isChecked = true;
  }
}

/**
 * Drives the "programme pré-rempli par l'IA" variant of the training form:
 * which fields the assistant filled, and the source document shown next to them.
 */
export const useTrainingPrefillStore = defineStore("trainingPrefill", {
  state: (): ITrainingPrefillState => ({
    isActive: false,
    mode: null,
    document: null,
    sourceUrl: null,
    aiFields: [],
    isViewerOpen: true,
  }),
  getters: {
    /** A field keeps its highlight and its ✦ marker until the admin edits it. */
    isPrefilled: (state) => (field: TrainingPrefillFieldEnum) => state.isActive && state.aiFields.includes(field),
    /** Either a PDF document or a source URL makes the viewer available. */
    hasDocument: (state) => !!state.document || !!state.sourceUrl,
  },
  actions: {
    /**
     * Opens the training form on the review the assistant streamed. Create: the wizard, on the
     * draft the backend already inserted (saving upserts it — never a second row). Update: edit
     * mode on the stored training, with only the changed proposals written on top.
     * Resolves to `false` when the form could not open (nothing was changed).
     */
    async openImportedTraining(prefill: ITrainingPrefill): Promise<boolean> {
      const createTraining = useCreateTrainingStore();

      createTraining.$reset();
      createTraining.trainingId = prefill.trainingId;

      if (prefill.mode === AiCompanionReviewModeEnum.Update) {
        // Stored values first: the upsert sends the whole form, so a field the document did not
        // mention must keep what the training already has.
        const loaded = await createTraining.loadTraining(prefill.trainingId);
        const type = createTraining.training.general.type;

        if (!loaded || createTraining.readonly || !type || !REVIEWABLE_TRAINING_TYPES.includes(type)) {
          createTraining.$reset();
          useFeedback().error("Cette formation ne peut pas être modifiée depuis l'assistant", "small");

          return false;
        }
        // Before `showModal`: TrainingModal's `isEdit` watcher only reacts to a change after mount.
        createTraining.isEdit = true;
      } else {
        // Everything the assistant creates is an external training (cf. Figma, "Traiter un programme de formation").
        createTraining.training.general.type = TrainingTypeEnum.EXTERNAL;
        const defaultAdmin = createTraining.buildDefaultTrainingAdmin();
        if (defaultAdmin) createTraining.training.general.trainingAdmins.push(defaultAdmin);
      }

      await applyValues(prefill.values);

      this.document = prefill.document;
      this.sourceUrl = prefill.sourceUrl ?? null;
      this.mode = prefill.mode;
      this.aiFields = [...prefill.fields];
      this.isActive = true;
      this.isViewerOpen = !!prefill.document || !!this.sourceUrl;

      createTraining.nav = TrainingNavEnum.GENERAL;
      createTraining.showModal = true;

      return true;
    },
    /** Called when the admin edits a pre-filled field: the value is theirs now, drop the marker. */
    clearField(field: TrainingPrefillFieldEnum) {
      if (!this.aiFields.includes(field)) return;

      this.aiFields = this.aiFields.filter((aiField) => aiField !== field);
    },
    toggleViewer() {
      this.isViewerOpen = !this.isViewerOpen;
    },
    openViewer() {
      this.isViewerOpen = true;
    },
    reset() {
      this.$reset();
    },
  },
});
